import { useState } from "react";
import SectionHead from "./SectionHead";
import { rankMerged, rankPeriods, rankTabs } from "@/mocks/stories";

function trendMeta(trend: string) {
  if (trend === "up") {
    return {
      cls: "text-primary-600",
      ico: "ri-arrow-up-line",
      bar: "bg-primary-500",
    };
  }
  if (trend === "down") {
    return { cls: "text-secondary-400", ico: "ri-arrow-down-line", bar: "bg-secondary-300" };
  }
  return { cls: "text-accent-600", ico: "ri-subtract-line", bar: "bg-accent-500" };
}

export default function HeatRank() {
  const [period, setPeriod] = useState("今日");
  const rows = rankPeriods[period] ?? [];

  return (
    <section id="heat" className="scroll-mt-24 bg-background-50">
      <div className="mx-auto w-full max-w-[1240px] px-4 py-10 md:px-6 md:py-16">
        <SectionHead
          no="02"
          kicker="热度飙升榜 · HEAT RANK"
          title="谁在风口浪尖"
          sub="综合全平台热搜、讨论声量与转载速度，按时段计算发酵热度。"
        />

        <div className="grid grid-cols-1 gap-5 lg:grid-cols-3">
          <div className="lg:col-span-2">
            <div className="overflow-hidden rounded-lg border border-secondary-200 bg-background-100">
              {rankMerged.map((r, i) => {
                const m = trendMeta(r.trend);
                const top = i < 3;
                return (
                  <article
                    key={r.title}
                    className={`flex items-center gap-4 px-4 py-3.5 transition-colors hover:bg-background-50 md:px-5 ${
                      i !== rankMerged.length - 1 ? "border-b border-secondary-200" : ""
                    }`}
                  >
                    <span
                      className={`font-heading grid w-10 shrink-0 place-items-center text-2xl font-black leading-none md:text-3xl ${
                        top ? "text-primary-500" : "text-secondary-300"
                      }`}
                    >
                      {String(i + 1).padStart(2, "0")}
                    </span>
                    <div className="min-w-0 flex-1">
                      <div className="flex flex-wrap items-center gap-2">
                        <span className="whitespace-nowrap rounded bg-background-200 px-1.5 py-0.5 text-[11px] font-semibold text-foreground-700">
                          {r.cat}
                        </span>
                        <h4 className="truncate text-sm font-semibold text-foreground-950 md:text-[15px]">
                          {r.title}
                        </h4>
                      </div>
                      <p className="mt-1 truncate text-xs text-secondary-600">
                        <i className="ri-question-answer-line mr-1 text-xs" />
                        {r.note} · {r.comments} 条讨论
                      </p>
                    </div>
                    <div className="shrink-0 text-right">
                      <p className="text-base font-black text-foreground-950 md:text-lg">{r.heat}</p>
                      <p className={`mt-0.5 inline-flex items-center gap-1 text-xs font-bold ${m.cls}`}>
                        <i className={m.ico} />
                        {r.delta}
                      </p>
                    </div>
                  </article>
                );
              })}
            </div>
            <p className="mt-3 flex items-center gap-2 text-[11px] text-secondary-500">
              <i className="ri-information-line" />
              榜单口径 · 微博 / 抖音 / 贴吧 / 知乎 等平台热度与讨论综合加权，每 15 分钟刷新。
            </p>
          </div>

          <div className="rounded-lg border border-secondary-200 bg-background-100 p-4 md:p-5">
            <div className="mb-4 flex flex-wrap items-center gap-1">
              {rankTabs.map((t) => (
                <button
                  key={t}
                  type="button"
                  onClick={() => setPeriod(t)}
                  className={`px-1 py-1 text-sm ${
                    period === t
                      ? "font-bold text-primary-600"
                      : "font-medium text-secondary-500 hover:text-foreground-800"
                  }`}
                >
                  {t}
                </button>
              ))}
              <span className="ml-auto text-[11px] text-secondary-400">分时回顾</span>
            </div>

            <ol className="space-y-3">
              {rows.map((r, i) => (
                <li key={r.title} className="flex items-start gap-2.5 border-b border-secondary-200 pb-3 last:border-0 last:pb-0">
                  <span className="font-heading text-base font-black text-secondary-400">{i + 1}</span>
                  <div className="min-w-0">
                    <h5 className="line-clamp-2 text-[13px] font-medium leading-5 text-foreground-800">{r.title}</h5>
                    <p className="mt-1 text-[11px] text-secondary-500">
                      {r.cat} · {r.comments} 讨论
                    </p>
                  </div>
                </li>
              ))}
            </ol>

            <a
              href="#progress"
              className="mt-5 flex w-full items-center justify-center gap-2 whitespace-nowrap rounded-md border border-secondary-300 py-2.5 text-sm font-semibold text-foreground-800 transition-colors hover:border-primary-400 hover:text-primary-600"
            >
              展开各事件完整榜
              <i className="ri-arrow-right-s-line text-base" />
            </a>
          </div>
        </div>
      </div>
    </section>
  );
}